import { ExportToCsv } from 'export-to-csv';
import P from '@leverege/promise';
import { GlobalState } from '@leverege/ui-redux';
import { format, addMonths } from 'date-fns';
import { Interface } from '@leverege/api-redux';
export async function generateBillingReport(locations, {
  month,
  year
}) {
  const headers = ['Report date', 'Company', 'location', 'CRM ID', 'Total Bill', 'Total Locators in Location', 'Locators Paired', 'Total Sales', 'RecovR Sales', 'Prior Voided Sales', 'Sell-Through (%)', 'Service Fee', 'Pending Vehicle Sales', 'Pending RecovR Sales', 'Total Lost Locators (Previous Billing Period)', 'Lost Locators - Confirmed Drive Off  (Previous Billing Period)', 'Lost Locators - Assumed Drive Offs  (Previous Billing Period)', 'Lost Locators - Support Required  (Previous Billing Period)', 'Lost - Offsite  (Previous Billing Period)'];
  const data = [];
  await P.mapLimit(locations, async location => {
    const locationId = location.id;
    const systemId = location.systemId;
    const res = await getBill(systemId, locationId, {
      month,
      year
    });
    const bill = res[0];
    if (bill) {
      const dataEntry = {
        reportDate: bill.data?.name || '',
        company: location.companyName || '',
        location: location.name || '',
        crmId: bill.data?.invoiceId || '',
        totalBill: bill.data?.totalBill || '',
        totalLocators: bill.data?.locator?.numAvailable || '',
        locatorsPaired: bill.data?.locator?.numPaired || '',
        totalSales: bill.data?.sales?.totalSales || '',
        recovrSales: bill.data?.sales?.recovrSales || '',
        priorVoidedSales: bill.data?.sales?.priorVoidedSales || '',
        sellThrough: bill.data?.sellThroughRate || '',
        serviceFee: bill.data?.serviceFee || '',
        pendingVehicleSales: bill.data?.sales?.pendingVehicleSales || '',
        pendingRecovrSales: bill.data?.sales?.pendingRecovrSales || '',
        numLost: bill.data?.locator?.numLost || ''
      };
      data.push(dataEntry);
    }
  }, {
    handleReject: false,
    limit: 2
  });
  const options = {
    fieldSeparator: ',',
    quoteStrings: '"',
    decimalSeparator: '.',
    showLabels: true,
    showTitle: true,
    useTextFile: false,
    useBom: true,
    headers,
    filename: 'Billing_Report'
  };
  const csvExporter = new ExportToCsv(options);
  if (!data.length) {
    data.push({});
  }
  csvExporter.generateCsv(data);
}
export function getBillChildrenFromGlobalState(obj, childname) {
  const data = GlobalState.get().models.data;
  const suffix = `${obj.id}/${childname}`;
  return data[`${obj.systemId}/company/${obj.data?.company?.id}/locations/${obj.data?.location?.id}/bills/${suffix}`] || data[`${obj.systemId}/company/${obj.data?.company?.id}/bills/${suffix}`] || data[`${obj.systemId}/location/${obj.data?.location?.id}/bills/${suffix}`] || data[`${obj.systemId}/bill/${suffix}`];
}
export function getTotalPayments(obj) {
  const paymentsAgg = GlobalState.get().models.data[`${obj.systemId}/payment`]?.totalPaymentsAgg;
  const data = paymentsAgg?.aggs?.byBill?.buckets.find(bucket => bucket.key === obj.id);
  const sum = data?.payments?.sum;
  const paymentAggData = getBillChildrenFromGlobalState(obj, 'payments')?.totalPaymentsAgg?.aggs?.payments;
  return paymentAggData?.sum || sum || 0;
}
export function getTotalCreditAddenda(obj) {
  const creditsAgg = GlobalState.get().models.data[`${obj.systemId}/addendum`]?.totalAddendumCreditsAgg;
  const data = creditsAgg?.aggs?.byBill?.buckets.find(bucket => bucket.key === obj.id);
  const sum = data?.credits?.sum;
  const addendumCreditAggData = getBillChildrenFromGlobalState(obj, 'addendum')?.totalAddendumCreditsAgg?.aggs?.credits;
  return addendumCreditAggData?.sum || sum || 0;
}
export function getTotalChargeAddenda(obj) {
  const chargesAgg = GlobalState.get().models.data[`${obj.systemId}/addendum`]?.totalAddendumChargesAgg;
  const data = chargesAgg?.aggs?.byBill?.buckets.find(bucket => bucket.key === obj.id);
  const sum = data?.charges?.sum;
  const addendumChargeAggData = getBillChildrenFromGlobalState(obj, 'addendum')?.totalAddendumChargesAgg?.aggs?.charges;
  return addendumChargeAggData?.sum || sum || 0;
}
export function calculateTotalCharges(obj) {
  // Factor in both the base cost of the original invoice plus any charge addenda
  const baseInvoiceAmount = obj?.data?.totalBill || 0;
  return baseInvoiceAmount + getTotalChargeAddenda(obj);
}
export function calculateTotalCredits(obj) {
  // Factor in both the total amount of any payments received plus any credit addend
  return getTotalPayments(obj) + getTotalCreditAddenda(obj);
}
export function calculateTotalDue(obj) {
  return calculateTotalCharges(obj) - calculateTotalCredits(obj);
}
export function getInvoiceId(location, time = new Date(), monthsToAdd = 1) {
  const locationNetworkId = 'recovr-dealer';
  const penId = location?.data?.networkAliases?.[locationNetworkId]?.pen;

  // in k8s this should be 0
  const offsetMs = time.getTimezoneOffset() * 60 * 1000;
  // set locale time to current UTC time
  const utcTime = new Date(time.getTime() + offsetMs);

  // If something happens in August, it'll be on the September 1st invoice by default.
  // Other charges, such as lost locators, appear in later invoices
  const utcPlusMonth = addMonths(utcTime, monthsToAdd);

  // assume monthly billing that always happens on the first of the month
  const timeString = format(utcPlusMonth, "MM-'01'-yyyy");
  return `${penId}-${timeString}`;
}
export async function getBill(systemId, locationId, {
  month,
  year
}) {
  const action = Interface.create(systemId, 'location').obj(locationId).grp('bills').search({
    filter: {
      type: 'logical',
      operator: 'and',
      conditions: [{
        type: 'equals',
        field: 'data/month',
        value: [month]
      }, {
        type: 'equals',
        field: 'data/year',
        value: [year]
      }]
    }
  });
  const {
    items
  } = await GlobalState.dispatch(action);
  return items;
}